import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { AlarmClock, Loader2, Mail, ChevronDown, RotateCcw } from 'lucide-react';
import { TemplatePreview } from './TemplatePreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_VARS = ['yetkili', 'firma', 'belge', 'gecerlilik_tarihi', 'kalan_gun'];

export const TemplateEditor = ({ label, prefix, vars, templates, defaults, onChange }) => {
  const subjectKey = `${prefix}_subject`;
  const bodyKey = `${prefix}_body`;
  const insert = (v) => onChange({ [bodyKey]: `${templates[bodyKey] || ''}{${v}}` });
  return (
    <div className="space-y-2" data-testid={`template-${prefix}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600">{label}</p>
        <button type="button" onClick={() => onChange({ [subjectKey]: defaults[subjectKey], [bodyKey]: defaults[bodyKey] })} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid={`template-reset-${prefix}`}>
          <RotateCcw className="h-3 w-3" />Varsayılana dön
        </button>
      </div>
      <Input value={templates[subjectKey] || ''} onChange={(e) => onChange({ [subjectKey]: e.target.value })} placeholder="E-posta konusu" data-testid={`template-subject-${prefix}`} />
      <Textarea value={templates[bodyKey] || ''} onChange={(e) => onChange({ [bodyKey]: e.target.value })} rows={7} className="text-sm" data-testid={`template-body-${prefix}`} />
      <div className="flex flex-wrap gap-1">
        {vars.map(v => (
          <button key={v} type="button" onClick={() => insert(v)} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 font-mono" title="Metne ekle">{`{${v}}`}</button>
        ))}
      </div>
      <TemplatePreview subject={templates[subjectKey] || ''} body={templates[bodyKey] || ''} testId={prefix} />
      {['permit', 'permit_end_reminder'].includes(prefix) && !(templates[bodyKey] || '').includes('{detaylar}') && (
        <p className="text-[11px] text-amber-600">{'{detaylar}'} kaldırılırsa personel, iş ve ekipman tablosu e-postada yer almaz.</p>
      )}
    </div>
  );
};

export const TemplateSection = ({ children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t pt-3">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-1.5 text-sm text-blue-700 hover:underline" data-testid={`${testId}-template-toggle`}>
        <Mail className="h-3.5 w-3.5" />E-posta içeriğini düzenle<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-4 mt-3">{children}</div>}
    </div>
  );
};

const ReminderRow = ({ title, desc, enabled = true, days, maxDays, onEnabled, onDays, testId, note, children }) => (
  <div className={`rounded-lg border p-4 transition-colors ${enabled ? 'bg-white' : 'bg-slate-50'}`} data-testid={testId}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="font-medium text-slate-800">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
      {onEnabled && <Switch checked={enabled} onCheckedChange={onEnabled} data-testid={`${testId}-switch`} />}
    </div>
    <div className={`flex items-center gap-2 mt-3 ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
      <Input type="number" min={1} max={maxDays} value={days} onChange={(e) => onDays(e.target.value)} className="w-24" data-testid={`${testId}-days`} />
      <span className="text-sm text-slate-600">gün önceden bildirim gönder</span>
    </div>
    {note && <p className="text-xs text-blue-700 bg-blue-50 rounded-md px-3 py-2 mt-3 flex items-center gap-1.5" data-testid={`${testId}-note`}><Mail className="h-3.5 w-3.5 shrink-0" />{note}</p>}
    {children && <TemplateSection testId={testId}>{children}</TemplateSection>}
  </div>
);

export const ReminderSettingsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/reminders`).then(res => setCfg(res.data)).catch(() => {});
    axios.get(`${API}/company-statuses`).then(res => setStatuses(res.data)).catch(() => {});
  }, []);

  if (!cfg) return null;
  const set = (patch) => setCfg(prev => ({ ...prev, ...patch }));
  const setTemplates = (patch) => setCfg(prev => ({ ...prev, templates: { ...prev.templates, ...patch } }));
  const tplProps = { templates: cfg.templates, defaults: cfg.default_templates, onChange: setTemplates };
  const toggleStatus = (id, checked) => set({
    reminder_company_status_ids: checked ? [...cfg.reminder_company_status_ids, id] : cfg.reminder_company_status_ids.filter(x => x !== id),
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/reminders`, {
        doc_reminder_enabled: cfg.doc_reminder_enabled, permit_reminder_enabled: cfg.permit_reminder_enabled,
        reminder_company_status_ids: cfg.reminder_company_status_ids, templates: cfg.templates,
        doc_reminder_days: Number(cfg.doc_reminder_days), permit_reminder_days: Number(cfg.permit_reminder_days),
      });
      setCfg(res.data);
      toast.success('Hatırlatma ayarları kaydedildi');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="reminder-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><AlarmClock className="h-5 w-5" />Otomatik Hatırlatma E-postaları</CardTitle>
        <CardDescription>Her sabah 07:00'de kontrol edilir ve firma yetkilisine e-posta gönderilir.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ReminderRow
          testId="doc-reminder" title="Belge geçerlilik hatırlatması"
          desc="Onaylı firma belgesinin geçerlilik tarihi yaklaşınca ve dolduğunda bildirim gönderilir."
          enabled={cfg.doc_reminder_enabled} days={cfg.doc_reminder_days} maxDays={365}
          onEnabled={(v) => set({ doc_reminder_enabled: v })} onDays={(v) => set({ doc_reminder_days: v })}
        >
          <TemplateEditor label="Süre dolmadan önce gönderilen e-posta" prefix="doc_soon" vars={DOC_VARS} {...tplProps} />
          <TemplateEditor label="Süre dolduğunda gönderilen e-posta" prefix="doc_expired" vars={DOC_VARS} {...tplProps} />
        </ReminderRow>
        <ReminderRow
          testId="permit-reminder" title="Çalışma izni bitiş hatırlatması"
          desc="Aktif veya onay bekleyen talebin bitiş tarihi yaklaşınca personel, iş ve ekipman detaylarıyla bildirim gönderilir."
          days={cfg.permit_reminder_days} maxDays={60} onDays={(v) => set({ permit_reminder_days: v })}
          note='Açma/kapama, e-posta içeriği, önizleme ve test: aşağıdaki "Sistem E-postaları" listesinde, Çalışma İzni Talepleri grubunda.'
        />
        <div className="rounded-lg border p-4">
          <p className="font-medium text-slate-800">Bildirim gidecek firma statüleri</p>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">Sadece seçili statülerdeki firmalara hatırlatma gönderilir. Hiç seçim yapılmazsa tüm firmalara gider.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {statuses.map(st => (
              <label key={st.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`reminder-status-${st.id}`}>
                <Checkbox checked={cfg.reminder_company_status_ids.includes(st.id)} onCheckedChange={(c) => toggleStatus(st.id, !!c)} data-testid={`reminder-status-checkbox-${st.id}`} />
                {st.name}
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3" data-testid="reminder-status-summary">
            {cfg.reminder_company_status_ids.length ? `${cfg.reminder_company_status_ids.length} statü seçili` : 'Seçim yok: tüm firmalar'}
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="reminder-settings-save">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Kaydet
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
